import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react';
import { rejectCodes } from '@ourbranch/lookups';

import useErrors from 'common/hooks/use-errors';
import { brandConstants } from 'brand-config';
import { Badge } from 'core/components/badge';

import { track } from 'core/helpers/analytics';
import { Button } from 'core/components/button';
import { useStore } from 'core/store';
import { Label } from 'core/components/label';
import Checkbox from 'core/components/checkbox';
import { Tooltip } from 'core/components/tooltip';
import Rejection from 'core/assets/svg/ic-rejection.svg';
import Solution from 'core/assets/svg/ic-solution.svg';
import StepsToResolve from 'core/assets/svg/ic-steps-to-resolve.svg';
import { generateGoogleMapsLink, generateZillowLink } from 'core/helpers/property-view-links';
import useSession from 'core/hooks/use-session';
import { getErrorData } from 'common/constants/get-error-data';
import { getFarmProductLabel } from 'common/helpers/farm-helpers';
import { ConfirmOverrideModal } from 'common/components/confirm-override-modal';
import { ResubmitOfferModal } from './resubmit-offer-modal';
import useStyles from './policy-errors.styles';

const typeToName = {
  home: 'Home',
  auto: 'Auto',
  renters: 'Renters',
  condo: 'Condo',
  monoline: 'Monoline auto',
  'monoline-home': 'Monoline home',
  'monoline-condo': 'Monoline condo'
};

const fixableAutoError = [rejectCodes.NO_CARS, rejectCodes.NEED_PRIOR_AUTO_COVERAGE_DETAILS];
const fixableHomeError = rejectCodes.VERISK_NO360;

const PolicyErrors = observer(() => {
  const router = useHistory();
  const classes = useStyles();
  const {
    offer: store,
    quote,
    account: { sendEventAsMember }
  } = useStore();
  const {
    isAgency,
    isInternalAgent,
    isInternalSales,
    canClearUWFormRejections,
    user,
    canSimulateSource,
    canOverrideAllRejections
  } = useSession();
  const { handleSubmit, values, setFieldValue, setFieldTouched } = useFormikContext();

  const [confirmModalData, setConfirmModalData] = useState(null);
  const [dataIsInaccurateAttestation, setDataIsInaccurateAttestation] = useState(false);
  const [openResubmitOfferModal, setOpenResubmitOfferModal] = useState(null);

  const allErrorsAndRejections = useErrors(store.errors, (code) => code >= 4003);
  const hasPriorCoverageError = allErrorsAndRejections.some(
    (e) => e.code === rejectCodes.NEED_PRIOR_AUTO_COVERAGE_DETAILS
  );
  // Hide the "no VIN" (50001) error when prior auto coverage (50099) is also present —
  // fixing 50099 first avoids a false requote failure, and 50001 will reappear if still relevant
  const errorsAndRejections = hasPriorCoverageError
    ? allErrorsAndRejections.filter((e) => e.code !== rejectCodes.NO_CARS)
    : allErrorsAndRejections;

  // Group same error codes that appear across multiple product types (ex: 50099 on both home and auto)
  // so they render as a single card with a combined title like "Auto and Home ineligible: ..."
  const dedupedErrors = [];
  for (const error of errorsAndRejections) {
    const existing = dedupedErrors.find((e) => e.code === error.code);
    if (existing) {
      existing.types.push(error.type);
    } else {
      dedupedErrors.push({ ...error, types: [error.type] });
    }
  }

  const onRequoteClick = (errorData) => {
    let offerToPrefill = store.offer;

    // We need to clear prior auto coverage information
    if (store.offer?.quote?.global?.isRC1PriorAutoCoverageDefaultsSet) {
      offerToPrefill = {
        ...store.offer,
        quote: {
          ...store.offer.quote,
          autoCoverage: {
            ...store.offer.quote.autoCoverage,
            policyLimitBIPD: null
          },
          global: {
            ...store.offer.quote.global,
            continuousAutoCoverageStartDate: null
          },
          insuranceInPast31Days: null
        }
      };
    }

    quote.setPrefillData(offerToPrefill, false, true);

    track('Re-run Offer', {
      errorCode: errorData.code,
      errorMessage: errorData.title,
      offerId: store.offer.id,
      staffUsername: user.username,
      isAgency,
      webUserId: store.offer.webUserId,
      offerType: store.offer.quote.selectedOption
    });

    router.push('/quote');
  };

  const onOverride = async (rejectionData) => {
    const errorsList = values.overrides?.overridesForUW?.length ? [...values.overrides.overridesForUW] : [];
    errorsList.push({
      code: parseInt(rejectionData.code, 10),
      overridingAgent: user.username,
      type: rejectionData.type
    });
    setFieldValue('overrides.overridesForUW', errorsList);
    setFieldTouched('overrides.overridesForUW', true);
    await setFieldValue('fcraDisclaimer', true);
    await setFieldTouched('fcraDisclaimer', true);

    handleSubmit();

    const dataToSend = {
      rejectionCode: rejectionData.code,
      rejectionReason: rejectionData.message,
      impactedPolicyType: store.offer?.quote?.selectedOption,
      offerId: store.offer?.id,
      isUnderwriting: canClearUWFormRejections,
      username: user.username,
      isAgency,
      email: store.offer?.quote?.email,
      phone: store.offer?.quote?.phone,
      logEmail: brandConstants.MEMBER_COMMS_EMAIL
    };

    await sendEventAsMember({
      eventName: 'Underwriting Rejection Overridden - Applicant',
      userId: store.offer?.webUserId,
      properties: JSON.stringify(dataToSend)
    });

    await track('Underwriting Rejection Overridden', dataToSend);
  };

  return (
    <>
      <Label id="policyErrors" type="darkGreenMedium" className={classes.header} component="div">
        Offer Rejections
        <Tooltip
          text="Offer rejections are rejections associated to a specific policy type that cannot be offered due to certain characteristics of the asset insured or the insurer. A rejection, however, does not affect the price of eligible policy types. If you want more information about ineligible policy characteristics see impact factors."
          placement="top"
          label="More info"
        />
      </Label>

      {confirmModalData !== null && (
        <ConfirmOverrideModal
          error={confirmModalData}
          onClose={() => {
            setConfirmModalData(null);
          }}
          canOverrideWithoutSaving
        />
      )}

      {openResubmitOfferModal && (
        <ResubmitOfferModal
          rejectionData={openResubmitOfferModal}
          onClose={() => {
            setOpenResubmitOfferModal(false);
          }}
        />
      )}

      <div id="offer_rejections">
        {dedupedErrors.map((error) => {
          const rejectionData = getErrorData({
            error,
            isAgency,
            state: store.state,
            errorType: 'REJECTION',
            // Farm-system offers get the initial-risk wording on the 50006 card and fix button. #36621
            policyType: store.offer?.quote?.selectedOption,
            googleLink: generateGoogleMapsLink(store.offer?.quote?.correctedAddress),
            zillowLink: generateZillowLink(store.offer?.quote?.correctedAddress),
            canClearUWFormRejections
          });
          const hasFixableError = [...fixableAutoError, fixableHomeError].includes(error.code);

          // A farm-type rejection names the offer's product: Farm, Home (Z) or Rental (Y). #37089
          const typeLabel = error.types
            .map((t) => (t === 'farm' ? getFarmProductLabel(store.offer?.quote?.selectedOption) : typeToName[t]))
            .filter(Boolean)
            .join(' and ');
          const title = `${typeLabel} ${[50006, 50001].includes(error.code) ? '' : 'ineligible'}: ${rejectionData.title}`;

          const showPermissionOverride =
            (rejectionData.canBeOverriddenWithPermissionGroup && canClearUWFormRejections) ||
            (canOverrideAllRejections && !rejectionData?.blockUniversalOverride);

          return (
            <div className={classes.card} key={`offer-error-${error.code}`}>
              <div className={classes.row}>
                <img src={Rejection} alt="rejection" />
                <div className={classes.titleColumn}>
                  <div className={classes.code}>{`rejection: ${rejectionData.code}`.toUpperCase()}</div>
                  {error.simulated && canSimulateSource && <Badge className={classes.simulatedBadge}>simulated</Badge>}
                  <div className={classes.title}>{title}</div>
                  {isInternalAgent && isInternalSales && rejectionData?.branchPlusEligible && (
                    <div className={classes.branchPlusEligible}>
                      Branch Plus eligible
                      <Tooltip
                        text="Although we may not be able to write this coverage due to this reason, our partnered providers typically do."
                        placement="top"
                      />
                    </div>
                  )}
                </div>
              </div>
              {/* subtitle */}
              {rejectionData.subtitle && <div className={classes.subtitle}>{rejectionData.subtitle}</div>}

              {/* why box */}
              {rejectionData.why && (
                <div className={classes.subBox}>
                  <div className={classes.iconRow}>
                    <img src={Solution} alt="why?" className={classes.icon} /> WHY?
                  </div>
                  <div className={classes.boxContent}>{rejectionData.why}</div>
                </div>
              )}

              {/* steps to resolve box */}
              {rejectionData.nextSteps && (
                <div className={classes.subBox}>
                  <div className={classes.iconRow}>
                    <img src={StepsToResolve} alt="Steps To Resolve" className={classes.icon} /> STEPS TO RESOLVE
                  </div>
                  <div className={classes.boxContent}>{rejectionData.nextSteps}</div>
                </div>
              )}

              {showPermissionOverride && (
                <Button
                  className={classes.overrideButton}
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    setConfirmModalData(rejectionData);
                  }}
                >
                  <Label>Override error</Label>
                </Button>
              )}

              {rejectionData.anyoneCanOverride && !showPermissionOverride && (
                <>
                  <Checkbox
                    id="overrideAttestation"
                    name="overrideAttestation"
                    label="I attest that the data flagging this property is inaccurate"
                    value={dataIsInaccurateAttestation}
                    labelClassName={classes.overrideCheckbox}
                    onChange={(_, checked) => {
                      setDataIsInaccurateAttestation(checked);
                    }}
                  />
                  {dataIsInaccurateAttestation && (
                    <Button
                      className={classes.inaccurateButton}
                      color="secondary"
                      onClick={() => {
                        onOverride(rejectionData);
                      }}
                    >
                      <Label>This is inaccurate</Label>
                    </Button>
                  )}
                </>
              )}

              {(rejectionData.canAnyoneRerunOffer || canClearUWFormRejections) && (
                <Button
                  className={classes.overrideButton}
                  variant="contained"
                  color="secondary"
                  onClick={() =>
                    hasFixableError ? setOpenResubmitOfferModal(rejectionData) : onRequoteClick(rejectionData)
                  }
                >
                  <Label>{rejectionData?.buttonLabel || 'Resubmit offer with accurate details'}</Label>
                </Button>
              )}

              {/* 
            // this is the code for florida soft rejections uncomment this when we open florida back up
            {canClearUWFormRejections &&
            store.state === 'FL' &&
            (floridaHomeSoftRejectCodes.includes(error.code) || floridaAutoSoftRejectCodes.includes(error.code)) && (
              <Button
                className={classes.overrideButton}
                variant="contained"
                color="primary"
                disabled={viewOnly}
                onClick={() => {
                  setConfirmModalData(rejectionData);
                }}
              >
                Override error
              </Button>
            )}
             */}
            </div>
          );
        })}
      </div>
    </>
  );
});

export default PolicyErrors;
